import React, { useMemo, useState, Fragment } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  PaginationState,
  RowSelectionState,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import {
  Card,
  CardFooter,
  CardHeader,
  CardHeading,
  CardTable,
  CardToolbar
} from "../../shared/ui/components/card";
import {
  DataGridTable,
  DataGridTableRowSelect,
  DataGridTableRowSelectAll
} from "../../shared/ui/components/data-grid-table";
import { DataGrid, useDataGrid } from "../../shared/ui/components/data-grid";
import { Badge } from "../../shared/ui/components/badge";
import { DataGridColumnHeader } from "../../shared/ui/components/data-grid-column-header";
import { Button } from "../../shared/ui/components/button";
import { Filter, Download, Edit, Trash2, Plus } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from "../../shared/ui/components/dropdown-menu";
import { ScrollArea, ScrollBar } from "../../shared/ui/components/scroll-area";
import { labelScrollRegion } from "../../shared/ui/lib/labelScrollRegion";
import { DataGridPagination } from "../../shared/ui/components/data-grid-pagination";
import { UseDocumentsReturn } from "../hooks/useDocuments";
import {
  DocumentDto,
  DocumentFilterDto,
  DocumentSortFieldDto,
  SortDirectionDto
} from "../types";
import { Skeleton } from "../../shared/ui/components/skeleton";
import { Alert } from "../../shared/ui/components/alert";
import { formatFileSize, downloadFile } from "../utils/fileUtils";
import { formatDate } from "../utils/dateUtils";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

interface DocumentsTableProps {
  documents: UseDocumentsReturn;
  onDocumentClick?: (document: DocumentDto) => void;
  onAddDocument?: () => void;
}

const DocumentsTable = ({
  documents: documentsApi,
  onDocumentClick,
  onAddDocument
}: DocumentsTableProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedFilter, setSelectedFilter] = useState<DocumentFilterDto>(
    DocumentFilterDto.All
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [documentToDelete, setDocumentToDelete] = useState<DocumentDto | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    string | null
  >(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const {
    documents,
    loading,
    error,
    totalCount,
    page,
    pageSize,
    totalPages,
    sortField,
    sortDirection,
    updateFilters,
    deleteDocument,
    getDownloadUrl
  } = documentsApi;

  const handleFilterChange = (value: string) => {
    const filterValue = value as DocumentFilterDto;
    setSelectedFilter(filterValue);
    updateFilters({ filter: filterValue });
  };

  const handlePageChange = (newPage: number) => {
    updateFilters({ page: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateFilters({ pageSize: newPageSize, page: 1 });
  };

  const handleDeleteClick = (document: DocumentDto) => {
    setDocumentToDelete(document);
  };

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocument(documentToDelete.documentId);
      setDocumentToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDocumentToDelete(null);
  };

  const handleDownload = async (document: DocumentDto) => {
    setDownloadingDocumentId(document.documentId);
    setDownloadError(null);

    try {
      // First, get the download URL from the backend
      // This will return either a public URL or a signed URL depending on document visibility
      const urlResponse = await getDownloadUrl(document.documentId);

      if (!urlResponse || !urlResponse.url) {
        throw new Error("No download URL returned from server");
      }

      // Now download the file using the URL from the backend
      await downloadFile(urlResponse.url, document.filename);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download document";
      setDownloadError(errorMessage);
      console.error("Download failed:", error);
    } finally {
      setDownloadingDocumentId(null);
    }
  };

  const columns = useMemo<ColumnDef<DocumentDto>[]>(
    () => [
      {
        accessorKey: "documentId",
        accessorFn: (row) => row.documentId,
        header: () => <DataGridTableRowSelectAll />,
        cell: ({ row }) => <DataGridTableRowSelect row={row} />,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        size: 51,
        meta: {
          cellClassName: ""
        }
      },
      {
        id: "filename",
        accessorFn: (row) => row.filename,
        header: ({ column }) => (
          <DataGridColumnHeader title="Filename" column={column} />
        ),
        cell: ({ row }) => (
          <button
            className="w-full text-left p-0 border-0 bg-transparent cursor-pointer group"
            onClick={() => onDocumentClick?.(row.original)}
            title={row.original.filename}
          >
            <div className="flex flex-col gap-0.5 py-1">
              <span className="font-medium text-sm text-mono group-hover:text-primary truncate block">
                {row.original.filename}
              </span>
              <span className="text-xs text-secondary-foreground">
                {row.original.fileSize
                  ? formatFileSize(row.original.fileSize)
                  : "-"}
              </span>
            </div>
          </button>
        ),
        enableSorting: true,
        size: 250,
        meta: {
          cellClassName: ""
        }
      },
      {
        id: "dateAdded",
        accessorFn: (row) => row.dateAdded,
        header: ({ column }) => (
          <DataGridColumnHeader title="Date Added" column={column} />
        ),
        cell: ({ row }) => (
          <span className="text-foreground font-normal">
            {formatDate(row.original.dateAdded)}
          </span>
        ),
        enableSorting: true,
        size: 180,
        meta: {
          cellClassName: ""
        }
      },
      {
        id: "publicStatus",
        accessorFn: (row) => row.isPublic,
        header: ({ column }) => (
          <DataGridColumnHeader title="Status" column={column} />
        ),
        cell: ({ row }) => {
          const isPublic = row.original.isPublic;
          return (
            <Badge
              size="lg"
              variant={isPublic ? "primary" : "warning"}
              appearance="light"
              shape="circle"
            >
              {isPublic ? "Public" : "Private"}
            </Badge>
          );
        },
        enableSorting: false,
        size: 120,
        meta: {
          cellClassName: ""
        }
      },
      {
        id: "comments",
        accessorFn: (row) => row.comments,
        header: ({ column }) => (
          <DataGridColumnHeader title="Comments" column={column} />
        ),
        cell: ({ row }) => (
          <div
            className="text-foreground font-normal max-w-full"
            title={row.original.comments || ""}
          >
            <span className="block truncate">
              {row.original.comments || "-"}
            </span>
          </div>
        ),
        enableSorting: false,
        size: 300,
        meta: {
          cellClassName: "max-w-[300px]"
        }
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDocumentClick?.(row.original)}
              title="Edit document"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDownload(row.original)}
              disabled={downloadingDocumentId === row.original.documentId}
              title={
                downloadingDocumentId === row.original.documentId
                  ? "Downloading..."
                  : "Download document"
              }
              aria-label={`Download ${row.original.filename}`}
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDeleteClick(row.original)}
              title="Delete document"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
        enableSorting: false,
        size: 120,
        meta: {
          cellClassName: ""
        }
      }
    ],
    [onDocumentClick, handleDownload, downloadingDocumentId]
  );

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: pageSize
  };

  const sorting: SortingState = useMemo(() => {
    if (!sortField || !sortDirection) {
      return [];
    }

    let columnId: string;
    switch (sortField) {
      case DocumentSortFieldDto.Filename:
        columnId = "filename";
        break;
      case DocumentSortFieldDto.DateAdded:
        columnId = "dateAdded";
        break;
      case DocumentSortFieldDto.FileSize:
        columnId = "fileSize";
        break;
      case DocumentSortFieldDto.UploadedBy:
        columnId = "uploadedBy";
        break;
      default:
        return [];
    }

    return [
      {
        id: columnId,
        desc: sortDirection === SortDirectionDto.Desc
      }
    ];
  }, [sortField, sortDirection]);

  const table = useReactTable({
    columns,
    data: documents,
    pageCount: totalPages,
    getRowId: (row: DocumentDto) => String(row.documentId),
    state: {
      pagination,
      sorting,
      rowSelection
    },
    columnResizeMode: "onChange",
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === "function" ? updater(pagination) : updater;
      handlePageChange(newPagination.pageIndex + 1);
      if (newPagination.pageSize !== pagination.pageSize) {
        handlePageSizeChange(newPagination.pageSize);
      }
    },
    onSortingChange: (updater) => {
      const newSort =
        typeof updater === "function" ? updater(sorting) : updater;

      if (newSort.length > 0) {
        const sort = newSort[0];
        let newSortField: DocumentSortFieldDto;

        switch (sort.id) {
          case "filename":
            newSortField = DocumentSortFieldDto.Filename;
            break;
          case "dateAdded":
            newSortField = DocumentSortFieldDto.DateAdded;
            break;
          case "fileSize":
            newSortField = DocumentSortFieldDto.FileSize;
            break;
          case "uploadedBy":
            newSortField = DocumentSortFieldDto.UploadedBy;
            break;
          default:
            updateFilters({
              sortField: undefined,
              sortDirection: undefined
            });
            return;
        }

        updateFilters({
          sortField: newSortField,
          sortDirection: sort.desc
            ? SortDirectionDto.Desc
            : SortDirectionDto.Asc
        });
      } else {
        updateFilters({
          sortField: undefined,
          sortDirection: undefined
        });
      }
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true
  });

  const Toolbar = () => {
    const { table } = useDataGrid();

    return (
      <CardToolbar>
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="text-sm text-muted-foreground">
            {totalCount} total documents
          </span>
        </div>
      </CardToolbar>
    );
  };

  if (loading && documents.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardHeading>
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-10 w-32" />
            </div>
          </CardHeading>
        </CardHeader>
        <CardTable>
          <div className="p-8">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4 py-4">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </CardTable>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardTable>
          <div className="p-8">
            <Alert variant="destructive">
              <div className="flex flex-col gap-2">
                <span className="font-medium">Error loading documents</span>
                <span className="text-sm">{error}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="w-fit"
                >
                  Reload page
                </Button>
              </div>
            </Alert>
          </div>
        </CardTable>
      </Card>
    );
  }

  return (
    <Fragment>
      <DataGrid
        table={table}
        recordCount={totalCount}
        tableLayout={{
          columnsPinnable: false,
          columnsMovable: false,
          columnsVisibility: false,
          cellBorder: true
        }}
      >
        <Card>
          <CardHeader>
            <CardHeading>
              <div className="flex items-center gap-2.5">
                <Button variant="primary" onClick={onAddDocument}>
                  <Plus className="h-4 w-4" />
                  Add Document
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Filter />
                      Filter
                      {selectedFilter !== DocumentFilterDto.All && (
                        <Badge
                          size="sm"
                          variant={
                            selectedFilter === DocumentFilterDto.Public
                              ? "primary"
                              : "warning"
                          }
                          appearance="light"
                          shape="circle"
                        >
                          {selectedFilter}
                        </Badge>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-40" align="start">
                    <DropdownMenuLabel>Document Filter</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={selectedFilter}
                      onValueChange={handleFilterChange}
                    >
                      {Object.values(DocumentFilterDto).map((filter) => (
                        <DropdownMenuRadioItem key={filter} value={filter}>
                          {filter}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeading>
            <Toolbar />
          </CardHeader>
          <CardTable>
            {downloadError && (
              <div className="p-4">
                <Alert variant="destructive">
                  <div className="flex flex-col gap-2">
                    <span className="font-medium">Download Error</span>
                    <span className="text-sm">{downloadError}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDownloadError(null)}
                      className="w-fit"
                    >
                      Dismiss
                    </Button>
                  </div>
                </Alert>
              </div>
            )}
            <ScrollArea viewportRef={labelScrollRegion("Documents")}>
              <DataGridTable />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardTable>
          <CardFooter>
            <DataGridPagination />
          </CardFooter>
        </Card>
      </DataGrid>

      <DeleteConfirmationModal
        isOpen={!!documentToDelete}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        document={documentToDelete}
        isDeleting={isDeleting}
      />
    </Fragment>
  );
};

export { DocumentsTable };
